import React, { useState, useEffect, useRef } from "react";
import { db, storage, auth } from "../../firebase";
import { signOut , onAuthStateChanged } from "firebase/auth";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./admin.css";

export default function AdminDashboard() {
  const [blogs, setBlogs] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [editId, setEditId] = useState(null);

  const blogsRef = collection(db, "blogs");
  const formRef = useRef(null);

  const fetchBlogs = async () => {
    const snapshot = await getDocs(blogsRef);
    setBlogs(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        window.location.href = "/admin";
      }
    });
  
    fetchBlogs();
  
    return () => unsubscribe();
  }, []);

  const uploadImage = async (file) => {
    if (!auth.currentUser) {
      alert("Please log in again — your session expired.");
      throw new Error("User not authenticated");
    }
  
    const fileRef = ref(storage, `blogs/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };
  

  const nameToUrl = (title) =>
    title
      ?.toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleSave = async () => {
    try {
      let imageUrl = null;
      if (file) {
        imageUrl = await uploadImage(file);
      }

      const urlKey = nameToUrl(title);

      if (editId) {
        const blogDocRef = doc(db, "blogs", editId);
        const existingDoc = await getDoc(blogDocRef);

        await updateDoc(blogDocRef, {
          title,
          content,
          urlKey,
          ...(imageUrl && { imageUrl }),
          createdAt: existingDoc.exists()
            ? existingDoc.data().createdAt
            : serverTimestamp(),
        });
        setEditId(null);
      } else {
        await addDoc(blogsRef, {
          title,
          content,
          urlKey,
          imageUrl: imageUrl || "",
          createdAt: serverTimestamp(),
        });
      }

      setTitle("");
      setContent("");
      setFile(null);
      fetchBlogs();
    } catch (error) {
      console.error("Error saving blog:", error);
      alert(error.message);
    }
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "blogs", id));
    fetchBlogs();
  };

  const handleEdit = (blog) => {
    setEditId(blog.id);
    setTitle(blog.title);
    setContent(blog.content);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/admin";
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <h2>Piercing Blog Dashboard</h2>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </header>

      {/* Blog Form */}
      <section ref={formRef} className="admin-form">
        <h3>{editId ? "Edit Blog" : "Add New Blog"}</h3>
        <input
          type="text"
          placeholder="Blog Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <ReactQuill
          value={content}
          onChange={setContent}
          placeholder="Write your blog content here..."
          className="quill-editor"
          modules={{
            toolbar: [
              [{ header: [1, 2, false] }],
              ["bold", "italic", "underline", "strike"],
              [{ list: "ordered" }, { list: "bullet" }],
              ["link"],
              ["clean"],
            ],
          }}
          formats={[
            "header",
            "bold",
            "italic",
            "underline",
            "strike",
            "list",
            "bullet",
            "link",
            "image",
          ]}
        />
        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          className="file-input"
        />
        <button onClick={handleSave} className="save-btn">
          {editId ? "Update Blog" : "Add Blog"}
        </button>
      </section>

      {/* Blog List */}
      <section className="blog-list">
        <h3>Published Blogs</h3>
        {blogs.length === 0 && <p>No blogs available.</p>}
        {blogs.map((blog) => (
          <div key={blog.id} className="blog-card">
            {blog.imageUrl && (
              <img src={blog.imageUrl} alt={blog.title} className="blog-image" />
            )}
            <h4>{blog.title}</h4>
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
            <div className="action-buttons">
              <button onClick={() => handleEdit(blog)} className="edit-btn">
                Edit
              </button>
              <button
                onClick={() => handleDelete(blog.id)}
                className="delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
